// The pure, thrash-stabilized client-side facing derivation (design D3, UI.md §8). The
// wire `facing` byte is a constant `0` (D-063), so the renderer derives each close-tier
// bee's directional frame from its inter-tick velocity via `headingIndex` — the
// previously-dead `draw-list.ts` utility this module finally wires in. Raw
// frame-per-velocity is jittery, so the derivation is stabilized three ways:
//
//   1. a min-speed threshold below which the last heading holds (guards u16-quantized
//      sub-pixel jitter);
//   2. angular hysteresis at the 30° frame boundaries (guards boundary oscillation);
//   3. a `Map<id, {heading, prevPos, ttl}>` of bounded-TTL tombstones that survive
//      `close_lod_cap` truncation churn AND a sequence gap so a dropped-then-reappearing
//      bee resumes its heading instead of snapping to frame 0.
//
// The tombstone TTL is denominated in **accepted close-tier sample frames**, not
// RAF/wall-clock frames: `onCloseFrame` is called exactly once per accepted delta/keyframe
// and is the only thing that ages the TTL, while `heading()` (read every RAF frame) is
// pure. Because a `GapWait` accepts no deltas, zero frames advance while it lasts, so a
// heading survives a full gap-recovery interval — the gap is the degenerate zero-frames
// case of the same truncation-churn TTL, needing no transient gap-state signal. A bare
// sequence gap (the render-input `streamEpoch` bumps, `connectionEpoch` does not) resets
// only the stale velocity basis so the first post-gap velocity is not computed across the
// discontinuity; a connection epoch (reconnect) clears the tombstones outright so stale
// headings are never carried across an identity discontinuity.
//
// Pure and Pixi-free: the renderer (group 11) feeds `onCloseFrame` on each accepted frame
// and reads `heading(id)` per drawable — the GPU wiring is exercised on-device only (D-086).

import { headingIndex } from './draw-list';

/** Minimum inter-frame displacement (world units) that counts as motion; below this the
 * last heading holds, rejecting u16-quantized sub-pixel jitter (~0.06 u at 4096/65536). */
export const DEFAULT_MIN_SPEED = 0.5;

/** Angular slack (degrees) added beyond the 15° bucket edge before the frame switches to
 * an adjacent heading — the anti-oscillation band at the 30° boundaries. */
export const DEFAULT_HYSTERESIS_DEG = 8;

/** How many accepted close-tier frames a dropped bee's heading survives before its
 * tombstone is evicted (bounded forgetting — the map cannot grow without eviction). At the
 * ~10 Hz close-tier cadence this is ~2 s of truncation-churn absence tolerance. */
export const DEFAULT_TOMBSTONE_TTL = 20;

/**
 * The heading a never-yet-moved bee faces, derived from its own id.
 *
 * This used to be a flat `0`, which points every one of them **up**. A bee only gets a derived
 * heading once it has moved far enough to clear the min-speed threshold, so anything stationary —
 * a hive full of idle bees, freshly spawned workers, a colony waiting on its first program — all
 * faced due north in unison, which reads as a rendering fault rather than as stillness (2026-08-11
 * finding; visible in the demo's spawn cues, and equally in live and rounds).
 *
 * `id * 5 mod 12` rather than `id mod 12`: 5 is coprime with 12, so consecutively-spawned ids
 * (exactly the case that produced the visible row of identical bees) land on maximally separated
 * headings instead of adjacent ones. Deterministic and stateless — the same bee always starts on
 * the same heading, so nothing here can differ between a rehearsal and a live run.
 *
 * **A non-positive id seeds to `0`, and that carve-out is load-bearing.** Real bee ids are positive;
 * the renderer also feeds this deriver *reserved* ids for things that are not bees — the own-hive
 * queen anchor rides `QUEEN_FACING_ID = -1` so that a queen pinned to a moving simulated position
 * can turn. A static anchor never clears the min-speed threshold, so whatever it is seeded with is
 * what it draws for ever. Scattering it gave the live Colony screen's queen `seededHeading(-1) = 5`
 * — about 150 degrees, down-left — permanently, silently changing shipped live and rounds rendering
 * away from design D4's frame 0, in a change whose own comment claimed live mode was unaffected.
 * The scatter exists to break up rows of freshly spawned *bees*; a single queen has no row to break
 * up and does have a canonical rest frame, so reserved ids get it.
 */
export function seededHeading(id: number): number {
  const whole = Math.trunc(id);
  if (whole <= 0) {
    return 0;
  }
  return (whole * 5) % 12;
}

/** The minimal per-bee position a frame carries for facing derivation. */
export interface FacingSample {
  readonly id: number;
  readonly position: readonly [number, number];
}

/** The two monotonic discontinuity counters the derivation reads off the render-input
 * (design D9) — a structural subset of `RenderInput`, so a `RenderInput` is accepted
 * directly. `connectionEpoch` clears tombstones (reconnect); a bare `streamEpoch` bump
 * (a sequence gap) resets only the velocity basis and holds the heading. */
export interface FacingEpochs {
  readonly connectionEpoch: number;
  readonly streamEpoch: number;
}

export interface FacingOptions {
  /** Tombstone lifetime in accepted close-tier frames (default `DEFAULT_TOMBSTONE_TTL`). */
  ttl?: number;
  /** Min-speed threshold in world units (default `DEFAULT_MIN_SPEED`). */
  minSpeed?: number;
  /** Angular hysteresis band in degrees (default `DEFAULT_HYSTERESIS_DEG`). */
  hysteresisDeg?: number;
}

interface FacingState {
  /** The current derived (or held) heading, 0..11. */
  heading: number;
  /** The previous accepted position — the velocity basis. `null` after an absence or a
   * gap reset, so the next appearance holds the heading rather than computing a stale
   * velocity across the discontinuity. */
  prevPos: readonly [number, number] | null;
  /** Accepted-frames remaining before eviction; refreshed to `ttl` whenever present. */
  ttl: number;
}

/** The compass-style angle (0° = "up", clockwise, 0..360) `headingIndex` quantizes —
 * recomputed here for the hysteresis boundary test. */
function angleDeg(vx: number, vy: number): number {
  const deg = (Math.atan2(vx, -vy) * 180) / Math.PI;
  return ((deg % 360) + 360) % 360;
}

export class FacingDeriver {
  private readonly ttl: number;
  private readonly minSpeedSq: number;
  private readonly hysteresisDeg: number;
  private readonly states = new Map<number, FacingState>();
  private lastConnectionEpoch = -1;
  private lastStreamEpoch = -1;
  private initialized = false;

  constructor(options: FacingOptions = {}) {
    this.ttl = options.ttl ?? DEFAULT_TOMBSTONE_TTL;
    const minSpeed = options.minSpeed ?? DEFAULT_MIN_SPEED;
    this.minSpeedSq = minSpeed * minSpeed;
    this.hysteresisDeg = options.hysteresisDeg ?? DEFAULT_HYSTERESIS_DEG;
  }

  /** The derived heading (0..11) for `id`, or `undefined` if never seen or already
   * evicted. Pure — reading never ages the TTL (aging is exactly-once per accepted frame,
   * in `onCloseFrame`), so the render loop can read it every RAF frame. */
  heading(id: number): number | undefined {
    return this.states.get(id)?.heading;
  }

  /** Number of tracked headings (present + tombstoned). Exposed so bounded-forgetting can
   * be asserted directly — the map shrinks on eviction, it does not grow without bound. */
  get size(): number {
    return this.states.size;
  }

  /**
   * Consume one accepted close-tier sample frame: derive/hold each present bee's heading,
   * refresh its tombstone, and age (then evict) the absent ones exactly once. This is the
   * ONLY method that advances the TTL — a `GapWait`, which produces no accepted frames,
   * calls it zero times and so freezes every tombstone.
   *
   * A `connectionEpoch` change (reconnect) clears the tombstones outright. A bare
   * `streamEpoch` change (a sequence gap, `connectionEpoch` unchanged) resets only the
   * velocity basis this frame — headings hold, and the first post-gap velocity is measured
   * from the recovered position, not across the gap.
   */
  onCloseFrame(samples: Iterable<FacingSample>, epochs: FacingEpochs): void {
    let resetBasis = false;
    if (!this.initialized) {
      // Adopt the first frame's epochs without treating them as a discontinuity.
      this.initialized = true;
      this.lastConnectionEpoch = epochs.connectionEpoch;
      this.lastStreamEpoch = epochs.streamEpoch;
    } else if (epochs.connectionEpoch !== this.lastConnectionEpoch) {
      // Reconnect / identity discontinuity: drop every tombstone so no stale heading
      // carries across. Subsumes the accompanying streamEpoch bump.
      this.states.clear();
      this.lastConnectionEpoch = epochs.connectionEpoch;
      this.lastStreamEpoch = epochs.streamEpoch;
      resetBasis = true;
    } else if (epochs.streamEpoch !== this.lastStreamEpoch) {
      // Bare sequence gap: hold the headings, reset only the velocity basis so the first
      // post-gap velocity is not computed across the discontinuity.
      this.lastStreamEpoch = epochs.streamEpoch;
      resetBasis = true;
    }

    const present = new Set<number>();
    for (const sample of samples) {
      present.add(sample.id);
      const pos: readonly [number, number] = [sample.position[0], sample.position[1]];
      const state = this.states.get(sample.id);
      let heading: number;
      if (state && !resetBasis && state.prevPos !== null) {
        const vx = pos[0] - state.prevPos[0];
        const vy = pos[1] - state.prevPos[1];
        if (vx * vx + vy * vy >= this.minSpeedSq) {
          heading = this.deriveWithHysteresis(vx, vy, state.heading);
        } else {
          heading = state.heading; // sub-threshold jitter → hold
        }
      } else {
        // First sight, a gap/reconnect basis reset, or a reappearance after an absence
        // (prevPos nulled): hold the tombstoned heading if present, else seed one from the bee's
        // own id. No velocity is computed this frame — the basis becomes this position.
        heading = state ? state.heading : seededHeading(sample.id);
      }
      this.states.set(sample.id, { heading, prevPos: pos, ttl: this.ttl });
    }

    // Age the tombstones of every bee absent this frame — exactly once per accepted frame.
    for (const [id, state] of this.states) {
      if (present.has(id)) continue;
      const nextTtl = state.ttl - 1;
      if (nextTtl <= 0) {
        this.states.delete(id); // bounded forgetting
      } else {
        state.ttl = nextTtl;
        state.prevPos = null; // a later reappearance holds the heading, not a stale velocity
      }
    }
  }

  /**
   * `headingIndex(vx, vy)` with angular hysteresis: near a 30° boundary the frame stays at
   * `currentHeading` until the angle clears the 15° bucket edge by `hysteresisDeg`,
   * preventing per-frame oscillation between the two adjacent frames. A large turn (well
   * past the band) switches immediately.
   */
  private deriveWithHysteresis(vx: number, vy: number, currentHeading: number): number {
    const raw = headingIndex(vx, vy); // wires in the previously-dead draw-list utility
    if (raw === currentHeading) return raw;
    const deg = angleDeg(vx, vy);
    const center = currentHeading * 30;
    let diff = (((deg - center) % 360) + 360) % 360;
    if (diff > 180) diff -= 360; // signed smallest angle, -180..180
    if (Math.abs(diff) < 15 + this.hysteresisDeg) return currentHeading;
    return raw;
  }
}
